// Per-questionnaire vendor status for the picker. Two independent signals:
//  - "admin returned" questions — sent back (rejected) and not yet reworked, or
//    the admin left the last comment in the thread. A vendor reply on a
//    sent-back question publishes the rework and clears this, even though the
//    status stays 'rejected'. These drive the red "needs your action" count
//    (isAdminReturned / adminReturnedActionCount, also used for category tab
//    badges). `pending` (answered, awaiting admin review) and approved / N-A are
//    NOT admin-returned.
//  - whether the vendor has finished the questionnaire — all visible required
//    questions complete — passed in by the caller as `fullyAnswered`.
// State precedence: admin-returned > not-finished > answered-but-unsent >
// under-review > compliant. So a questionnaire that was sent but isn't finished
// reads "awaiting your response" rather than a cleared status. (Admin-side
// badges live in outstandingActions.ts.)
import {
  isAwaitingVendor,
  reviewableSubmissions,
  reviewTurn,
  type ReviewTurn,
} from '@l2u/ui/lib/reviewState.ts';
import { isDocumentsTabDocument, type DocumentsTabQuestion } from '@l2u/ui/lib/documentsTab.ts';
import { isOwedByVendor } from '@l2u/ui/lib/outstandingActions.ts';
import type { CompletionReason } from '@l2u/ui/lib/visibility.ts';
import type { Question, Submission } from '@l2u/ui/lib/types.ts';

export type QuestionnaireVendorStatus = {
  actionCount: number;
  state:
    | 'action_needed'
    | 'awaiting_response'
    | 'pending_submission'
    | 'under_review'
    | 'compliant';
};

type BadgeSubmission = Pick<
  Submission,
  | 'status'
  | 'latest_comment_author'
  | 'question_id'
  | 'resubmitted_at'
  | 'reviewed_at'
  | 'reverify_requested_at'
>;

/**
 * True when the admin has returned this submission to the vendor for action.
 *
 * Delegates to the shared isAwaitingVendor rather than restating the rule: this
 * was a hand-copy of it, and it went stale when PR #64 replaced the "Submit to
 * admin" button with the thread reply. The reply stamps resubmitted_at but
 * leaves status on 'rejected' (which is what keeps the inputs editable),
 * so a `status === 'rejected'` test of its own kept the red count and the
 * "Action required" state lit for the rest of the round.
 *
 * A Documents-tab document (approved compliance doc, or one in a renewal cycle)
 * is never admin-returned here: its conversation is nagged about in Documents.
 */
export function isAdminReturned(
  question: DocumentsTabQuestion,
  sub: BadgeSubmission | undefined,
): boolean {
  if (!sub) return false;
  if (isDocumentsTabDocument(question, sub)) return false;
  return isAwaitingVendor(sub);
}

/**
 * The questions whose submissions decide this questionnaire's review turn.
 * Drops Documents-tab documents in a renewal cycle (not approved): they live,
 * and are nagged about, in Documents. Approved documents STAY: they are cleared
 * so they cannot drive the turn, but without them a questionnaire made only of
 * documents scopes to nothing and reviewTurn([]) reads in_progress.
 */
function questionnaireScoped(
  questions: Question[],
  subByQ: ReadonlyMap<string, BadgeSubmission>,
): Question[] {
  return questions.filter((q) => {
    const s = subByQ.get(q.id);
    return !(isDocumentsTabDocument(q, s) && s?.status !== 'approved');
  });
}

/** Count of questions (within the given set) the admin has returned to the vendor. */
export function adminReturnedActionCount(
  questions: Question[],
  subByQ: Map<string, BadgeSubmission>,
): number {
  let n = 0;
  for (const question of questions) {
    if (isAdminReturned(question, subByQ.get(question.id))) n += 1;
  }
  return n;
}

/**
 * Derive the vendor-action status for a single questionnaire. Pass the questions
 * already scoped to that questionnaire, the vendor's submission list, and two
 * gates the caller computes: `fullyAnswered` (computeProgress(...).progress ===
 * 100) and `submitted` (questionnaireSubmitted — keyed on submitted_at).
 *
 * Precedence, on the vendor's already-published data (unpublished admin drafts
 * are stripped server-side, so a verdict is only visible post-Notify):
 *  1. any admin-returned item  -> action_needed  ("Action required")
 *  2. not fully answered       -> awaiting_response
 *  3. answered but not submitted-> pending_submission
 *  4. submitted, no clearing verdict published yet -> under_review
 *  5. submitted, all items approved / N-A          -> compliant
 * reviewTurn splits 4 from 5; because the vendor never sees a draft verdict,
 * `compliant` is unreachable until the admin publishes (clicks Notify).
 */
/**
 * The `fullyAnswered` signal for questionnaireVendorStatus: every visible,
 * non-retired required question is complete — the submit gate's own test.
 * Not `progress === 100`: the percentage leaves compliance-exempt questions
 * out, so a blank declaration signature read "Pending submission" here while
 * the dashboard said "Needs your action" and submit still refused.
 */
export function isQuestionnaireFullyAnswered(
  questions: Question[],
  breakdown: {
    visibleIds: ReadonlySet<string>;
    completionByQuestion: Map<string, CompletionReason | null>;
  },
): boolean {
  return questions.every(
    (q) => !breakdown.visibleIds.has(q.id) || !isOwedByVendor(q, breakdown.completionByQuestion),
  );
}

export function questionnaireVendorStatus(
  questions: Question[],
  submissions: BadgeSubmission[],
  opts: { fullyAnswered: boolean; submitted: boolean; visibleIds: ReadonlySet<string> },
): QuestionnaireVendorStatus {
  const subByQ = new Map(submissions.map((s) => [s.question_id, s]));
  // Unscoped on purpose: the Documents-tab guard inside isAdminReturned is the
  // load-bearing one for the count.
  const actionCount = adminReturnedActionCount(questions, subByQ);
  if (actionCount > 0) return { actionCount, state: 'action_needed' };
  if (!opts.fullyAnswered) return { actionCount: 0, state: 'awaiting_response' };
  if (!opts.submitted) return { actionCount: 0, state: 'pending_submission' };
  // Compliance-exempt AND hidden questions are excluded: never reviewed, so
  // their submissions stay 'pending' and used to hold this at 'under_review'
  // for good.
  const scoped = reviewableSubmissions(
    questionnaireScoped(questions, subByQ),
    subByQ,
    opts.visibleIds,
  );
  const state = reviewTurn(scoped) === 'compliant' ? 'compliant' : 'under_review';
  return { actionCount: 0, state };
}

/**
 * Review turn for the banner at the top of ONE open questionnaire.
 *
 * Scoped to this questionnaire's questions so an admin-returned item in a
 * *different* questionnaire cannot raise the "review the marked questions"
 * banner here, and 'in_progress' (no banner) until the vendor has submitted —
 * autosaved answers exist as `pending` submissions while they are still filling
 * it in.
 *
 * Compliance-exempt and hidden questions are excluded: never reviewed, so their
 * submissions stay 'pending' and used to keep the banner on "Submitted — under
 * review by Admin" even after every reviewable item had been approved. A hidden
 * question's row outlives the parent answer flip that hid it.
 */
export function bannerReviewTurn(
  questions: Question[],
  submissions: BadgeSubmission[],
  opts: { submitted: boolean; visibleIds: ReadonlySet<string> },
): ReviewTurn {
  if (!opts.submitted) return 'in_progress';
  const subByQ = new Map(submissions.map((s) => [s.question_id, s]));
  return reviewTurn(
    reviewableSubmissions(questionnaireScoped(questions, subByQ), subByQ, opts.visibleIds),
  );
}
